import React, { FC } from 'react'

import { RewardsLevel, RewardsQuery } from '../../../../../graphql/search'
import {
  maxRebatePercentage,
  maxVipDiscountPercentage,
  RewardsLevelInfo,
  rewardsLevelsInfo,
  tierFromRewardsLevel,
} from '../../../../../lib/rewards/levels'
import { dropZeroCents, formatAsCurrencyWithCommas } from '../../../../../utils/priceTools'
import * as Styled from './styled'

export interface RewardsTierTableProps {
  currentSnapshot?: NonNullable<
    NonNullable<RewardsQuery['customer']>['rewardsSnapshots']
  >['nodes'][number]
}

const RewardsTierModalBodyRow: FC<
  RewardsLevelInfo & { currentLevel: RewardsLevel; index: number }
> = ({
  currentLevel,
  index,
  level,
  salesThreshold: { minimum: salesMinimum, maximum: salesMaximum },
  benefits: { vipDiscountPercentage, rebatePercentage, commissionsPercentage },
}) => {
  const tier = tierFromRewardsLevel(level)
  const isCurrentLevel = level === currentLevel
  if (tier == null) {
    return null
  }
  return (
    <Styled.TableBodyRow key={`level-${level}`} $isCurrentLevel={isCurrentLevel} $rowIndex={index}>
      <Styled.TableBodyFirstCell>
        <Styled.TierNameCellWrapper $rowIndex={index}>
          <Styled.Highlight>Tier {tier}</Styled.Highlight>
          <Styled.TierSalesThreshold>
            {salesMaximum
              ? `sales up to ${dropZeroCents(formatAsCurrencyWithCommas({ amount: salesMaximum }))}`
              : `sales over ${dropZeroCents(formatAsCurrencyWithCommas({ amount: salesMinimum }))}`}
          </Styled.TierSalesThreshold>
        </Styled.TierNameCellWrapper>
      </Styled.TableBodyFirstCell>
      <Styled.TableBodyCell $isCurrentLevel={isCurrentLevel}>
        <Styled.Percentage $isMaxValue={vipDiscountPercentage === maxVipDiscountPercentage}>
          {vipDiscountPercentage.toFixed(1)}%
        </Styled.Percentage>
      </Styled.TableBodyCell>
      <Styled.TableBodyCell $isCurrentLevel={isCurrentLevel}>
        <Styled.Percentage $isMaxValue={rebatePercentage === maxRebatePercentage}>
          {rebatePercentage.toFixed(1)}%
        </Styled.Percentage>
      </Styled.TableBodyCell>
      <Styled.TableBodyCell $isCurrentLevel={isCurrentLevel}>
        <Styled.Percentage $isMaxValue={false}>
          {commissionsPercentage.toFixed(1)}%
        </Styled.Percentage>
      </Styled.TableBodyCell>
    </Styled.TableBodyRow>
  )
}

export const RewardsTierTable: FC<RewardsTierTableProps> = ({ currentSnapshot }) => {
  const currentLevel = currentSnapshot?.level ?? RewardsLevel.Level_1
  return (
    <>
      <Styled.TableHeaderRow>
        <Styled.TableHeaderFirstCell>
          <Styled.Highlight>Rewards Tier</Styled.Highlight>
          per monthly sales
        </Styled.TableHeaderFirstCell>
        <Styled.TableHeaderCell>
          <Styled.Highlight>VIP Discount on In-Practice Orders</Styled.Highlight>
        </Styled.TableHeaderCell>
        <Styled.TableHeaderCell>
          <Styled.Highlight>Monthly Rebate</Styled.Highlight>
          (on wholesale prices, excluding PL orders)
        </Styled.TableHeaderCell>
        <Styled.TableHeaderCell>
          <Styled.Highlight>Commissions on Patient-Direct Sales</Styled.Highlight>
        </Styled.TableHeaderCell>
      </Styled.TableHeaderRow>
      <Styled.TableBody>
        {rewardsLevelsInfo.map((definition, index) => (
          <RewardsTierModalBodyRow
            key={`level-${definition.level}`}
            currentLevel={currentLevel}
            index={index}
            {...definition}
          />
        ))}
      </Styled.TableBody>
    </>
  )
}
